import type { SupplierDetailResponse } from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Star } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { captureException } from '@/domains/observability'

import { favoriteSupplier, unfavoriteSupplier } from './api'

type Props = {
  slug: string
  isFavorited: boolean
}

/** favorite toggle for the supplier-profile hero. operates against the
 * detail query cache (where /suppliers/:slug fetches into) so the optimistic
 * fill flips instantly. onSettled also invalidates the list query so the
 * star in the table stays in sync when the user navigates back.
 */
export function FavoriteButton({ slug, isFavorited }: Props) {
  const queryClient = useQueryClient()
  const detailKey = ['supplier-directory', 'detail', slug]
  const listKey = ['supplier-directory', 'list']

  const mutation = useMutation({
    mutationFn: async (next: boolean) =>
      next ? favoriteSupplier(slug) : unfavoriteSupplier(slug),
    onMutate: async (next: boolean) => {
      await queryClient.cancelQueries({ queryKey: detailKey })
      const previous =
        queryClient.getQueryData<SupplierDetailResponse>(detailKey)
      if (previous) {
        queryClient.setQueryData<SupplierDetailResponse>(detailKey, {
          ...previous,
          isFavorited: next
        })
      }
      return { previous }
    },
    onError: (error, _next, context) => {
      if (context?.previous) {
        queryClient.setQueryData(detailKey, context.previous)
      }
      captureException(error)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: detailKey })
      void queryClient.invalidateQueries({ queryKey: listKey })
    }
  })

  return (
    <Button
      type='button'
      variant='outline'
      size='hero'
      onClick={() => mutation.mutate(!isFavorited)}
      aria-pressed={isFavorited}
    >
      <Star
        className={`${isFavorited ? 'fill-amber-400 text-amber-500' : ''}`}
      />
      {isFavorited ? 'Favorited' : 'Add to favorites'}
    </Button>
  )
}
